import React, { Component } from 'react';
import PropTypes from 'prop-types';
import config from 'cityads-v3/config';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import i18n from 'cityads-v3/i18n';
import { Helmet } from 'react-helmet';
import Marquee from "react-fast-marquee";

import ScrollToTopOnMount from 'cityads-v3/containers/misc/ScrollToTopOnMount';
import Balaclava from 'cityads-v3/components/Balaclava/Balaclava'
import Carousel from 'cityads-v3/components/Carousel/Carousel'
import ClickableCardWithPopup from 'cityads-v3/components/CardWithPopup/ClickableCardWithPopup'
import ContactFormCard from 'cityads-v3/components/Forms/ContactFormCard'
import ToolsCarousel, { ALLOWED_PATHS as TOOLS_PATHS } from 'cityads-v3/components/Carousel/ToolsCarousel'
import Typography from '@material-ui/core/Typography'

import StartWorkButton, { ALLOWED_PATHS as STARTWORK_PATHS } from 'cityads-v3/components/Buttons/StartWorkButton';

import { yandexReachGoal } from 'cityads-v3/utils/counterUtils';
import { sassExportsToObjectRecursive } from 'cityads-v3/utils/styleUtils';
import classNames from './Advertiser.scss';

import {
  USER_ROLE_CODE_ADVERTISER,
  USER_ROLE_CODE_ADVMADNET,
} from 'cityads-v3/reducers/auth';
import {
  ADVERTISER_PARTNERS_BY_LANGUAGE,
  ADVERTISER_LOGOS_BY_LANGUAGE,
} from './Landing.data.js';

const sassExports  = sassExportsToObjectRecursive(classNames);

// eslint-disable-next-line no-magic-numbers
const CITYADS_BIRTHDAY = new Date(2010, 8, 4);
const MSECS_IN_YEAR = 31536000000;

export const ALLOWED_PATHS = [
  ...STARTWORK_PATHS,
  ...TOOLS_PATHS,
  'more'
];

const partnerCardStyles = {
  ...sassExports.partnerCardStyles,
  cardHeaderClasses: {
    root: classNames.carouselHeader,
    title: classNames.carouselHeaderTitle,
    subheader: classNames.carouselSubheader,
  },
  cardSubheaderExpanded: classNames.carouselSubheaderExpanded,
  collapseClasses: {
    root: classNames.carouselCollapse,
    entered: classNames.carouselCollapseExpanded,
  },
};

@connect(
  state => ({
    user              : state.auth.user,
    toolsCarouselItems: state.home.toolsCarouselItems,
    language          : state.language.language,
  })
)
@withTranslation(['main'], { i18n, wait: false })
export default class Advertiser extends Component {
  static propTypes = {
    user              : PropTypes.object,
    toolsCarouselItems: PropTypes.array,
    language          : PropTypes.string,
    t                 : PropTypes.func
  }

  clickStartWork() {
    yandexReachGoal('CLICK_STARTWORK_BUTTON_ADVERTISER');
  }

  render () {
    const { t, match, language } = this.props;

    let subpagePrefix = match.path.split(':')[0];
    subpagePrefix = subpagePrefix.substr(0, subpagePrefix.length - 1);

    const now = new Date();
    const age = Math.floor((now - CITYADS_BIRTHDAY) / MSECS_IN_YEAR)

    return (
      <div className={classNames.advertiserPage}>
        <ScrollToTopOnMount/>

        <Helmet
          title = {t('advertiser_page.title')}
          meta  = {[
            { name: 'description', content: t('advertiser_page.description') },
            { name: 'keywords', content: t('advertiser_page.keywords') },
          ]}
        />
        <Balaclava
          menuValue = {'advertiser'}
        />

        <div className={classNames.cta}>
          <div className={classNames.ctaWrapper}>
            <div className={classNames.ctaText}>
              <div className={classNames.ctaPreHeader}>
                {t('ca_media')}
              </div>
              <h1 className={classNames.ctaHeader}>
                {t('advertiser_page.headerSlogan')}
              </h1>

              <div className={classNames.ctaContent}>
                {t('advertiser_page.headerText')}
              </div>

              <div className={classNames.ctaButton}>
                <StartWorkButton
                  label        = {t('advertiser_page.startWorkButton')}
                  user         = {this.props.user}
                  currentPath  = {subpagePrefix}
                  userRoleCode = {[USER_ROLE_CODE_ADVERTISER, USER_ROLE_CODE_ADVMADNET]}
                  registerAs   = "advertiser"
                  onClick      = {this.clickStartWork}
                />
              </div>
            </div>

            <div className={classNames.ctaCarouselContainer}>
              <Carousel
                className        = {classNames.ctaCarousel}
                items            = {ADVERTISER_PARTNERS_BY_LANGUAGE[language] ?? ADVERTISER_PARTNERS_BY_LANGUAGE[config.DEFAULT_LANGUAGE]}
                limits           = {{ 0: 1 }}
                autoplay         = {true}
                isCardExpandable = {true}
                isStretched      = {false}
                cardProps        = {partnerCardStyles}
              />
            </div>
          </div>
        </div>


        <div className={classNames.advertisers}>
          <Marquee gradient={false}>
            {(ADVERTISER_LOGOS_BY_LANGUAGE[language] ?? ADVERTISER_LOGOS_BY_LANGUAGE[config.DEFAULT_LANGUAGE]).map((advertiser, idx) =>
              <div key={idx} className={classNames.advertisers__item}>
                <img alt={advertiser.title} src={advertiser.image} className={classNames.advertiser} />
              </div>
            )}
          </Marquee>
        </div>


        <div className={classNames.steps}>
          <h2 className={classNames.stepsHeader}>
            <span className={classNames.stepsHeaderWrapper} dangerouslySetInnerHTML={{__html: t('advertiser_page.steps')}} />
          </h2>

          <div className={classNames.stepsList}>
            <div className={classNames.stepRegister}>
              <Typography component="h3" className={classNames.stepName}>
                {t('advertiser_page.step1title')}
              </Typography>
              <Typography component="div">
                {t('advertiser_page.step1text')}
              </Typography>
            </div>

            <div className={classNames.stepIntegration}>
              <Typography component="h3" className={classNames.stepName}>
                {t('advertiser_page.step2title')}
              </Typography>
              <Typography component="div">
                {t('advertiser_page.step2text')}
              </Typography>
            </div>

            <div className={classNames.stepLaunch}>
              <Typography component="h3" className={classNames.stepName}>
                {t('advertiser_page.step3title')}
              </Typography>
              <Typography component="div">
                {t('advertiser_page.step3text')}
              </Typography>
            </div>
          </div>
        </div>


        <div className={classNames.numbers}>
          <div className={classNames.numbersText}>
            <div className={classNames.numbersHeader}>
              {t('advertiser_page.numbers')}
            </div>

            <div className={classNames.numbersList}>
              <div className={classNames.numbersItem}>
                <div className={classNames.number}>
                  {t('advertiser_page.number1')}
                </div>
                <div className={classNames.numberText}>
                  {t('advertiser_page.number1text')}
                </div>
              </div>

              <div className={classNames.numbersItem}>
                <div className={classNames.number}>
                  {t('advertiser_page.number2')}
                </div>
                <div className={classNames.numberText}>
                  {t('advertiser_page.number2text')}
                </div>
              </div>

              <div className={classNames.numbersItem}>
                <div className={classNames.number}>
                  {t('advertiser_page.number3', {count: age})}
                </div>
                <div className={classNames.numberText}>
                  {t('advertiser_page.number3text')}
                </div>
              </div>
            </div>
          </div>
        </div>


        <div className={classNames.features}>
          <div className={classNames.tools}>
            <ToolsCarousel
              className = {classNames.toolsCarousel}
              limits    = {{ 0: 1, 544: 2, 800: 4 }}
              items     = {this.props.toolsCarouselItems
                .filter(item => item.orderAdv > 0)
                .sort((item1, item2) => item1.orderAdv - item2.orderAdv)
                .map(item => ({
                  ...item,
                  title          : t('carouselItems.' + item.translateKey + '.title'),
                  children       : t('carouselItems.' + item.translateKey + '.subtitle'),
                  moreButtonTitle: t('learn_more'),
                }))}
              moreRoot  = {subpagePrefix}
              lessLink  = {subpagePrefix}
            />
          </div>

          <div className={classNames.featureList}>
            <div className={classNames.featureRow}>
              <div className={classNames.featureColStretcher}>
                <ContactFormCard
                  className = {classNames.feature}
                />
              </div>

              <div className={classNames.featureColStretcher}>
                <ClickableCardWithPopup
                  className       = {classNames.feature}
                  title           = {<h2 className={classNames.cardHeader}>{t('card9.title')}</h2>}
                  subtitle        = {t('card9.subtitle')}
                  image           = {require('./img/advertiser/coin.png')}
                  moreLink        = {subpagePrefix + '/more'}
                  lessLink        = {subpagePrefix}
                  moreButtonTitle = {t('more_info')}
                  articleClass    = "AdvertiserMore"
                >
                  {t('card9.text')}
                </ClickableCardWithPopup>
              </div>
            </div>
          </div>
        </div>


        <div className={classNames.successes}>
          <h2 className={classNames.successesHeader}>
            {t('publisher_page.successes')}
          </h2>

          <div className={classNames.partners}>
            <Carousel
              className        = {classNames.partnersCarousel}
              items            = {ADVERTISER_PARTNERS_BY_LANGUAGE[language] ?? ADVERTISER_PARTNERS_BY_LANGUAGE[config.DEFAULT_LANGUAGE]}
              limits           = {{ 0: 1, 500: 2, 700: 3 }}
              isCardExpandable = {true}
              isStretched      = {false}
              cardProps        = {partnerCardStyles}
            />
          </div>
        </div>
      </div>
    );
  }
}
